import { useEffect, useRef, useState } from "react";

import { PalettePicker } from "./PalettePicker.js";
import type {
  PaletteMarker,
  PalettePickerOptions,
  PaletteSample,
  PaletteSampleMarker,
  PaletteSamplePoint,
  PaletteSelection,
  PaletteSwatchColors,
  PaletteTemplate,
} from "./types.js";

type Props = {
  template: PaletteTemplate;
  texture?: boolean;
  debug?: boolean;
  selectedIndex?: number | null;
  swatchColors?: PaletteSwatchColors | undefined;
  samplePoint?: PaletteSamplePoint | null;
  markers?: PaletteMarker[];
  sampleMarkers?: PaletteSampleMarker[];
  className?: string;
  onSelect?: (selection: PaletteSelection) => void;
  onSample?: (sample: PaletteSample) => void;
};

export function PaletteCanvas({
  template,
  texture = false,
  debug = false,
  selectedIndex = null,
  swatchColors,
  samplePoint = null,
  markers = [],
  sampleMarkers = [],
  className,
  onSelect,
  onSample,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const pickerRef = useRef<PalettePicker | null>(null);
  const onSelectRef = useRef(onSelect);
  const onSampleRef = useRef(onSample);
  const [error, setError] = useState<string | null>(null);
  const [visibleSelectedIndex, setVisibleSelectedIndex] = useState<number | null>(selectedIndex);
  const [visibleSamplePoint, setVisibleSamplePoint] = useState<PaletteSamplePoint | null>(samplePoint);

  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);
  useEffect(() => { onSampleRef.current = onSample; }, [onSample]);
  useEffect(() => { setVisibleSelectedIndex(selectedIndex); }, [selectedIndex]);
  useEffect(() => { setVisibleSamplePoint(samplePoint); }, [samplePoint]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    setError(null);
    const options: PalettePickerOptions = {
      canvas,
      template,
      texture,
      debug,
      selectedIndex,
      swatchColors,
      markers,
      onError: (err) => setError(err.message),
    };
    options.onSelect = (selection) => {
      setVisibleSelectedIndex(selection.index);
      setVisibleSamplePoint(null);
      onSelectRef.current?.(selection);
    };
    options.onSample = (sample) => {
      setVisibleSelectedIndex(null);
      setVisibleSamplePoint({ x: sample.x, y: sample.y, hex: sample.hex });
      onSampleRef.current?.(sample);
    };
    const picker = new PalettePicker(options);
    pickerRef.current = picker;
    void picker.load().catch(() => undefined);
    return () => {
      picker.destroy();
      pickerRef.current = null;
    };
  }, [template]);

  useEffect(() => pickerRef.current?.setTexture(texture), [texture]);
  useEffect(() => pickerRef.current?.setDebug(debug), [debug]);
  useEffect(() => pickerRef.current?.setSelectedIndex(selectedIndex), [selectedIndex]);
  useEffect(() => {
    try {
      pickerRef.current?.setSwatchColors(swatchColors);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [swatchColors]);

  const selectedSwatch = visibleSelectedIndex === null
    ? null
    : template.swatches.find((swatch) => swatch.index === visibleSelectedIndex) ?? null;

  return (
    <div className={className}>
      {error && <p className="mb-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive">{error}</p>}
      {/* container-type lets the indicators below size in `cqw` (1% of the board's
          rendered width) so they stay proportional to the artwork on a narrow
          mobile board, capped via `min()` at their tuned desktop pixel sizes. */}
      <div className="relative" style={{ containerType: "inline-size" }}>
        <canvas
          ref={canvasRef}
          className="block h-auto w-full touch-none rounded-md border border-border bg-card"
          aria-label={template.name}
        />
        <div className="pointer-events-none absolute inset-0">
          {selectedSwatch && (
            <div
              className="absolute rounded-[calc(10px*var(--app-text-scale))] border-2 border-primary shadow-[0_0_0_3px_rgba(255,255,255,0.92),0_0_16px_rgba(0,0,0,0.35)]"
              style={{
                left: `${((selectedSwatch.x - 5) / template.imageWidth) * 100}%`,
                top: `${((selectedSwatch.y - 5) / template.imageHeight) * 100}%`,
                width: `${((selectedSwatch.width + 10) / template.imageWidth) * 100}%`,
                height: `${((selectedSwatch.height + 10) / template.imageHeight) * 100}%`,
                // Percentage radii resolve against the ring's own box, not the image.
                borderRadius: `${((selectedSwatch.radius + 6) / (selectedSwatch.width + 10)) * 100}% / ${((selectedSwatch.radius + 6) / (selectedSwatch.height + 10)) * 100}%`,
              }}
            >
              <span
                className="absolute right-2 top-2 rounded-full border-2 border-primary bg-white shadow-sm"
                style={{ width: "min(30px, 7.5cqw)", height: "min(30px, 7.5cqw)" }}
              />
            </div>
          )}
          {markers.map((marker) => {
            const swatch = template.swatches.find((item) => item.index === marker.index);
            if (!swatch) return null;
            const tl = marker.corner === "tl";
            const pointX = tl ? swatch.x + 21 : swatch.x + swatch.width - 21;
            const pointY = tl ? swatch.y + 21 : swatch.y + swatch.height - 21;
            return (
              <span
                key={`${marker.index}-${marker.corner ?? "br"}-${marker.label ?? ""}-${marker.color ?? ""}`}
                className="absolute flex items-center justify-center rounded-full border-2 border-white font-bold text-black shadow"
                style={{
                  left: `${(pointX / template.imageWidth) * 100}%`,
                  top: `${(pointY / template.imageHeight) * 100}%`,
                  width: "min(30px, 7.5cqw)",
                  height: "min(30px, 7.5cqw)",
                  fontSize: "calc(min(13.5px, 3.375cqw) * var(--app-text-scale))",
                  background: marker.color ?? "rgba(255,255,255,0.92)",
                  transform: "translate(-50%, -50%)",
                }}
              >
                {marker.label?.slice(0, 2)}
              </span>
            );
          })}
          {sampleMarkers.map((marker, index) => (
            <span
              key={`${marker.label ?? index}-${marker.x}-${marker.y}-${marker.hex}`}
              className="absolute flex items-center justify-center rounded-full border-2 border-white font-bold text-black shadow-[0_0_0_1px_rgba(0,0,0,0.45),0_0_12px_rgba(0,0,0,0.35)]"
              style={{
                left: `${(marker.x / template.imageWidth) * 100}%`,
                top: `${(marker.y / template.imageHeight) * 100}%`,
                width: marker.active ? "min(48px, 12cqw)" : "min(30px, 7.5cqw)",
                height: marker.active ? "min(48px, 12cqw)" : "min(30px, 7.5cqw)",
                fontSize: marker.active
                  ? "calc(min(15px, 3.75cqw) * var(--app-text-scale))"
                  : "calc(min(12px, 3cqw) * var(--app-text-scale))",
                background: marker.hex,
                transform: "translate(-50%, -50%)",
              }}
              title={marker.label}
            >
              {marker.label?.slice(0, 2)}
            </span>
          ))}
          {visibleSamplePoint && (
            <span
              className="absolute rounded-full border-[4px] border-white shadow-[0_0_0_2px_rgba(0,0,0,0.55),0_0_18px_rgba(0,0,0,0.45)]"
              style={{
                left: `${(visibleSamplePoint.x / template.imageWidth) * 100}%`,
                top: `${(visibleSamplePoint.y / template.imageHeight) * 100}%`,
                width: "min(48px, 12cqw)",
                height: "min(48px, 12cqw)",
                background: visibleSamplePoint.hex,
                transform: "translate(-50%, -50%)",
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
